import {SyncEventType} from '../types';
import {VideoSyncDebugger} from './videoSyncDebugger';

// smallest difference between video and playback state for a meaningful change
const TIME_SYNC_THRESHOLD = 0.1;

// Float-precision guard for paused videos — any seek larger than this syncs followers
const PAUSED_MIN_THRESHOLD = 0.01;

/**
 * Check if there are meaningful changes between video state and playback state.
 * This function is extracted for easier testing and reusability.
 */
export const checkForMeaningfulChanges = (
  video: HTMLVideoElement | null,
  playbackState: {
    currentTime: number;
    isPlaying: boolean;
    playbackRate: number;
    lastUpdate: number;
  }
): boolean => {
  if (!video) {
    return false;
  }
  if (playbackState.isPlaying !== !video.paused) {
    return true;
  }
  if (playbackState.playbackRate !== video.playbackRate) {
    return true;
  }
  const timeDifference = Math.abs(
    video.currentTime - playbackState.currentTime
  );

  if (video.paused) {
    return timeDifference > PAUSED_MIN_THRESHOLD;
  }
  // Balancing smooth playback with sync precision
  // More frequent adjustments means more flickering of the controls, and halting video progress
  return timeDifference > 0.25;
};

const shouldSyncTime = (
  videoElement: HTMLVideoElement,
  state: {currentTime: number}
) => {
  if (state.currentTime > videoElement.duration) {
    return videoElement.currentTime !== videoElement.duration;
  }

  const timeDifference = Math.abs(videoElement.currentTime - state.currentTime);

  if (videoElement.paused) {
    return timeDifference > PAUSED_MIN_THRESHOLD;
  }

  // When playing, use the standard threshold to prevent sync thrashing
  return timeDifference > TIME_SYNC_THRESHOLD;
};

/**
 * Apply synchronization state to a video element.
 * This function is extracted for easier testing and reusability.
 */
export const synchronizeVideo = (
  videoElement: HTMLVideoElement,
  videoId: string,
  eventType: SyncEventType,
  state: {
    isPlaying: boolean;
    currentTime: number;
    playbackRate: number;
  },
  isApplyingSyncRef: {
    current: {
      isPlaying: boolean;
      currentTime: number;
      playbackRate: number;
    } | null;
  },
  correlationId?: string,
  originalEventType?: string
) => {
  /**
   * Check if we actually need to make any changes before setting the sync flag.
   * This prevents unnecessary locking when no synchronization is needed.
   */
  const needsTimeSync = shouldSyncTime(videoElement, state);
  /**
   * TIME_UPDATE events fire frequently enough that do not want to
   * sync any other portion of the state when processing the event.
   */
  const needsPlayStateSync =
    originalEventType !== SyncEventType.TIME_UPDATE &&
    state.isPlaying !== !videoElement.paused;
  const needsRateSync =
    originalEventType !== SyncEventType.TIME_UPDATE &&
    videoElement.playbackRate !== state.playbackRate;

  // If no changes are needed, don't set the sync flag at all
  if (!needsTimeSync && !needsPlayStateSync && !needsRateSync) {
    VideoSyncDebugger.logSync(
      'no changes needed, skipping sync',
      videoId,
      originalEventType || eventType,
      state,
      correlationId
    );
    return;
  }

  /**
   * When applying updates from the context, we lock the state to prevent
   * overlapping updates causing thrashing.
   * This is checked in handleVideoEvent to prohibit these changes firing
   * events generated internally by the video element.
   * For example, when updating the time, the video element will internally
   * pause the video, seek to the correct time, and play the video.
   * Those events would create a loop of events that would cause thrashing.
   */

  // Prevent multiple sync operations from running simultaneously
  if (isApplyingSyncRef.current) {
    VideoSyncDebugger.logSync(
      'sync already in progress, skipping duplicate sync',
      videoId,
      originalEventType || eventType,
      state,
      correlationId
    );
    return;
  }

  // Store the target state in the ref so event handlers can check against it
  isApplyingSyncRef.current = state;
  const timeSyncDiff = needsTimeSync
    ? `${videoElement.currentTime} - ${state.currentTime} > `
    : 'no';
  const playStateSyncDiff = needsPlayStateSync
    ? `${!videoElement.paused} vs ${state.isPlaying}`
    : 'no';
  const rateSyncDiff = needsRateSync
    ? `${videoElement.playbackRate} vs ${state.playbackRate}`
    : 'no';
  VideoSyncDebugger.logSync(
    `sync operation started needs: time=${timeSyncDiff}, play=${playStateSyncDiff}, rate=${rateSyncDiff}, target state stored`,
    videoId,
    originalEventType || eventType,
    state,
    correlationId,
    videoElement
  );

  // We'll clear the flag immediately after all sync operations are complete

  if (needsTimeSync) {
    // duration may not be finite while the video is loading
    const safeCurrentTime = isFinite(state.currentTime) ? state.currentTime : 0;
    const safeDuration = isFinite(videoElement.duration)
      ? videoElement.duration
      : 0;
    const targetTime = Math.min(safeCurrentTime, safeDuration);

    if (isFinite(targetTime) && targetTime >= 0) {
      VideoSyncDebugger.logSync(
        `syncing ${videoElement.currentTime} to target ${targetTime}`,
        videoId,
        originalEventType || eventType,
        state,
        correlationId
      );
      videoElement.currentTime = targetTime;
    }
  }

  // Apply play/pause state AFTER seeking
  if (
    needsPlayStateSync &&
    state.isPlaying &&
    videoElement.paused &&
    !videoElement.ended
  ) {
    VideoSyncDebugger.logSync(
      'playing paused video',
      videoId,
      originalEventType || eventType,
      state,
      correlationId
    );
    videoElement.play().catch(() => {});
  } else if (needsPlayStateSync && !state.isPlaying && !videoElement.paused) {
    VideoSyncDebugger.logSync(
      'pausing playing video',
      videoId,
      originalEventType || eventType,
      state,
      correlationId
    );
    videoElement.pause();
  }

  // Apply playback rate
  if (needsRateSync) {
    VideoSyncDebugger.logSync(
      'syncing playback rate',
      videoId,
      originalEventType || eventType,
      state,
      correlationId
    );
    videoElement.playbackRate = state.playbackRate;
  }

  // Clear the sync target state after a small delay to prevent immediate re-triggering
  // This prevents sync loops while still allowing legitimate sync operations
  setTimeout(() => {
    isApplyingSyncRef.current = null;
    VideoSyncDebugger.logSync(
      'sync operation completed, target state cleared after delay',
      videoId,
      originalEventType || eventType,
      state,
      correlationId
    );
  }, 16); // One frame delay to prevent immediate re-triggering
};
